import React, { useState, useEffect } from 'react';
import { X, DollarSign, BarChart3, Settings, TrendingUp, Sparkles, SlidersHorizontal } from 'lucide-react';
import { useLogto } from '@logto/react';
import { apiPost } from '../utils/api';
import { API_ENDPOINTS } from '../config/apiConfig';

// Sliders and charts components

interface Channel {
  name: string;
  column: string;
}

interface ScenarioResult {
  name: string;
  allocation: Record<string, number>;
  predicted_response: number;
  change_vs_baseline?: number;
  change_pct_vs_baseline?: number;
  roi: number;
  contributions: Record<string, number>;
}

interface ScenarioPlannerModalProps {
  modelId: string;
  channels: Channel[];
  totalBudget?: number;
  currentAllocation?: Record<string, number>;  // Current/actual budget allocation for baseline comparison
  depVarName?: string;  // Name of dependent variable (e.g., "conversions", "revenue", "leads")
  onClose: () => void;
}

export const ScenarioPlannerModal: React.FC<ScenarioPlannerModalProps> = ({
  modelId,
  channels,
  totalBudget: initialBudget,
  currentAllocation: providedCurrentAllocation,
  depVarName = 'Revenue', // Default to Revenue for backward compatibility
  onClose,
}) => {
  // Determine if the dependent variable is monetary (for currency display)
  const monetaryVars = ['revenue', 'sales', 'cost', 'price', 'spend', 'profit'];
  const isMonetaryVar = monetaryVars.some(v => depVarName.toLowerCase().includes(v));
  const depVarDisplay = depVarName.charAt(0).toUpperCase() + depVarName.slice(1).toLowerCase();
  // Validate totalBudget - if it's suspiciously small (likely an iteration count, not budget), use default
  // Values between 50 and 500 are likely optimization iteration counts, not monetary budgets
  const isSuspiciousBudget = initialBudget !== undefined && initialBudget > 0 && initialBudget < 1000;
  const defaultBudget = !isSuspiciousBudget && initialBudget && initialBudget > 0 ? initialBudget : 100000;

  const [activeTab, setActiveTab] = useState<'recommended' | 'custom' | 'compare'>('recommended');
  const [totalBudget, setTotalBudget] = useState<number>(defaultBudget);
  const [showBudgetInput, setShowBudgetInput] = useState(initialBudget === undefined || isSuspiciousBudget);
  const [budgetInput, setBudgetInput] = useState<string>(defaultBudget.toString());
  const [recommendedAllocation, setRecommendedAllocation] = useState<Record<string, number> | null>(null);
  const [customAllocation, setCustomAllocation] = useState<Record<string, number>>({});
  const [baselineAllocation] = useState<Record<string, number> | undefined>(
    providedCurrentAllocation
  );
  const [scenarios, setScenarios] = useState<ScenarioResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const logto = useLogto();

  // Initialize custom allocation with equal split
  useEffect(() => {
    const equalSplit = channels.reduce((acc, channel) => {
      acc[channel.name] = totalBudget / channels.length;
      return acc;
    }, {} as Record<string, number>);
    setCustomAllocation(equalSplit);
  }, [channels, totalBudget]);

  // Fetch recommended allocation (only when budget is confirmed)
  useEffect(() => {
    const fetchRecommendedAllocation = async () => {
      try {
        setLoading(true);

        const data = await apiPost<{ result: { allocation: Record<string, number> } }>(
          API_ENDPOINTS.OPTIMIZE.BUDGET,
          logto,
          {
            model_id: modelId,
            total_budget: totalBudget,
          }
        );

        if (data.result?.allocation) {
          setRecommendedAllocation(data.result.allocation);
        }
      } catch (err) {
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Error fetching recommended allocation. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    // Only fetch when budget input is confirmed (showBudgetInput is false)
    if (!showBudgetInput) {
      fetchRecommendedAllocation();
    } else {
      setLoading(false);
      setRecommendedAllocation(null);
    }
  }, [modelId, totalBudget, showBudgetInput]);

  const handleAllocationChange = (channelName: string, value: number) => {
    setCustomAllocation(prev => ({
      ...prev,
      [channelName]: Math.max(0, value),
    }));
  };

  const handleAnalyzeScenarios = async () => {
    try {
      setLoading(true);
      setError(null);

      // Create scenario specs
      const scenarioSpecs = [
        {
          name: 'Recommended',
          allocation: recommendedAllocation || customAllocation,
          description: 'Optimizer recommended allocation'
        },
        {
          name: 'Custom',
          allocation: customAllocation,
          description: 'Your custom allocation'
        },
      ];

      console.log('[ScenarioPlanner] Analyzing scenarios:', { modelId, scenarioSpecs });

      const requestBody: Record<string, any> = {
        model_id: modelId,
        scenarios: scenarioSpecs,
      };

      // Include baseline allocation if available (current/actual budget for comparison)
      if (baselineAllocation) {
        requestBody.baseline_allocation = baselineAllocation;
        console.log('[ScenarioPlanner] Using baseline allocation:', baselineAllocation);
      } else {
        console.log('[ScenarioPlanner] No baseline allocation provided - API will use first scenario as fallback');
      }

      console.log('[ScenarioPlanner] Sending request:', requestBody);

      const data = await apiPost<{ scenarios: ScenarioResult[] }>(
        API_ENDPOINTS.SCENARIO.ANALYZE,
        logto,
        requestBody
      );

      console.log('[ScenarioPlanner] Response data:', data);

      setScenarios(data.scenarios || []);
      setActiveTab('compare');
    } catch (err) {
      console.error('[ScenarioPlanner] Error:', err);
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Error analyzing scenarios. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalBudgetUsed = (allocation: Record<string, number>) => {
    return Object.values(allocation).reduce((sum, val) => sum + val, 0);
  };

  const getChannelChange = (channelName: string) => {
    if (!recommendedAllocation) return 0;
    const custom = customAllocation[channelName] || 0;
    const recommended = recommendedAllocation[channelName] || 0;
    return custom - recommended;
  };

  const handleSetBudget = () => {
    try {
      const newBudget = parseFloat(budgetInput);
      if (isNaN(newBudget) || newBudget <= 0) {
        setError('Please enter a valid budget amount');
        return;
      }
      setTotalBudget(newBudget);
      setShowBudgetInput(false);
      setError(null);
    } catch (err) {
      setError('Invalid budget amount');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-blue-600" /> Scenario Planner
            </h2>
            <p className="text-sm text-gray-600 mt-1">Test different channel allocations and see predicted outcomes</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white rounded-lg transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Budget Input Dialog */}
        {showBudgetInput && (
          <div className="bg-yellow-50 border-b border-yellow-200 p-6">
            <h3 className="text-lg font-semibold text-yellow-900 mb-3">Enter Total Budget</h3>
            <p className="text-sm text-yellow-800 mb-4">We need to know your total budget to allocate across channels.</p>
            <div className="flex gap-3">
              <input
                type="number"
                value={budgetInput}
                onChange={(e) => setBudgetInput(e.target.value)}
                placeholder="Enter budget amount"
                className="flex-1 px-4 py-2 border border-yellow-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="0"
                step="100"
              />
              <button
                onClick={handleSetBudget}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* Tabs and Content (only show after budget is set) */}
        {!showBudgetInput && (
          <>
            {/* Tabs */}
            <div className="border-b border-gray-200 bg-gray-50">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('recommended')}
                  className={`px-6 py-3 font-medium transition-colors ${activeTab === 'recommended'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                    }`}
                >
                  <BarChart3 className="w-4 h-4 inline mr-1" /> Recommended
                </button>
                <button
                  onClick={() => setActiveTab('custom')}
                  className={`px-6 py-3 font-medium transition-colors ${activeTab === 'custom'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                    }`}
                >
                  <Settings className="w-4 h-4 inline mr-1" /> Custom
                </button>
                <button
                  onClick={() => setActiveTab('compare')}
                  className={`px-6 py-3 font-medium transition-colors ${activeTab === 'compare'
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                    }`}
                >
                  <TrendingUp className="w-4 h-4 inline mr-1" /> Compare
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {loading && <div className="text-center py-8 text-gray-500">Loading...</div>}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}

              {/* Recommended Tab */}
              {activeTab === 'recommended' && !loading && (
                <div className="space-y-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h3 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" /> AI-Optimized Allocation
                    </h3>
                    <p className="text-sm text-green-800">
                      Based on your model, this is the recommended allocation that maximizes predicted revenue.
                    </p>
                  </div>

                  {recommendedAllocation && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {channels.map(channel => (
                        <div key={channel.name} className="bg-white border border-gray-200 rounded-lg p-4">
                          <p className="font-semibold text-gray-900">{channel.name}</p>
                          <p className="text-2xl font-bold text-blue-600 mt-2">
                            ${(recommendedAllocation[channel.name] || 0).toLocaleString('en-US', {
                              maximumFractionDigits: 0,
                            })}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {(((recommendedAllocation[channel.name] || 0) / totalBudget) * 100).toFixed(1)}% of budget
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={handleAnalyzeScenarios}
                    className="w-full btn btn-primary"
                  >
                    Compare with Custom Allocation →
                  </button>
                </div>
              )}

              {/* Custom Tab */}
              {activeTab === 'custom' && !loading && (
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <SlidersHorizontal className="w-4 h-4" /> Adjust Allocation
                    </h3>
                    <p className="text-sm text-blue-800">
                      Use sliders or input fields to test different allocation strategies.
                    </p>
                  </div>

                  {/* Budget Summary */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <p className="text-xs text-gray-600 font-medium">Total Budget</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        ${totalBudget.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <p className="text-xs text-gray-600 font-medium">Allocated</p>
                      <p className="text-2xl font-bold text-blue-600 mt-1">
                        ${calculateTotalBudgetUsed(customAllocation).toLocaleString('en-US', {
                          maximumFractionDigits: 0,
                        })}
                      </p>
                    </div>
                    <div className="bg-white border border-gray-200 rounded-lg p-4">
                      <p className="text-xs text-gray-600 font-medium">Remaining</p>
                      <p className={`text-2xl font-bold mt-1 ${calculateTotalBudgetUsed(customAllocation) <= totalBudget
                        ? 'text-green-600'
                        : 'text-red-600'
                        }`}>
                        ${(totalBudget - calculateTotalBudgetUsed(customAllocation)).toLocaleString('en-US', {
                          maximumFractionDigits: 0,
                        })}
                      </p>
                    </div>
                  </div>

                  {/* Sliders */}
                  <div className="space-y-4">
                    {channels.map(channel => (
                      <div key={channel.name} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <label className="font-semibold text-gray-900">{channel.name}</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={Math.round(customAllocation[channel.name] || 0)}
                              onChange={e => handleAllocationChange(channel.name, parseFloat(e.target.value))}
                              className="w-24 px-3 py-1 border border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-600">
                              ({(((customAllocation[channel.name] || 0) / totalBudget) * 100).toFixed(1)}%)
                            </span>
                          </div>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max={totalBudget}
                          value={customAllocation[channel.name] || 0}
                          onChange={e => handleAllocationChange(channel.name, parseFloat(e.target.value))}
                          className="w-full"
                        />
                        {recommendedAllocation && (
                          <p className="text-xs text-gray-500 mt-1">
                            Recommended: ${(recommendedAllocation[channel.name] || 0).toLocaleString('en-US', {
                              maximumFractionDigits: 0,
                            })} {getChannelChange(channel.name) !== 0 && (
                              <span className={getChannelChange(channel.name) > 0 ? 'text-green-600' : 'text-red-600'}>
                                ({getChannelChange(channel.name) > 0 ? '+' : ''}${getChannelChange(channel.name).toLocaleString('en-US', { maximumFractionDigits: 0 })})
                              </span>
                            )}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={handleAnalyzeScenarios}
                    className="w-full btn btn-primary"
                    disabled={calculateTotalBudgetUsed(customAllocation) > totalBudget}
                  >
                    Analyze Scenarios →
                  </button>
                </div>
              )}

              {/* Compare Tab */}
              {activeTab === 'compare' && !loading && (
                <div className="space-y-6">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h3 className="font-semibold text-purple-900 mb-2 flex items-center gap-2">
                      <BarChart3 className="w-4 h-4" /> Scenario Comparison
                    </h3>
                    <p className="text-sm text-purple-800">
                      Compare predicted outcomes across different allocation strategies.
                    </p>
                  </div>

                  {scenarios.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {scenarios.map((scenario, idx) => (
                        <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4">
                          <h4 className="font-semibold text-gray-900 mb-3">{scenario.name}</h4>

                          <div className="space-y-3">
                            <div>
                              <p className="text-xs text-gray-600 font-medium">Predicted {depVarDisplay}</p>
                              <p className="text-2xl font-bold text-blue-600">
                                {isMonetaryVar ? '$' : ''}{scenario.predicted_response.toLocaleString('en-US', {
                                  maximumFractionDigits: 0,
                                })}
                              </p>
                              {scenario.change_pct_vs_baseline !== undefined && scenario.change_pct_vs_baseline !== 0 && (
                                <p className={`text-sm font-medium mt-1 ${scenario.change_pct_vs_baseline > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {scenario.change_pct_vs_baseline > 0 ? '↑' : '↓'} {Math.abs(scenario.change_pct_vs_baseline).toFixed(1)}% vs baseline
                                  {scenario.change_vs_baseline !== undefined && (
                                    <span className="text-xs text-gray-600 block mt-0.5">
                                      {scenario.change_vs_baseline > 0 ? '+' : ''} {isMonetaryVar ? '$' : ''}{scenario.change_vs_baseline.toLocaleString('en-US', {
                                        maximumFractionDigits: 0,
                                      })}
                                    </span>
                                  )}
                                </p>
                              )}
                            </div>

                            <div>
                              <p className="text-xs text-gray-600 font-medium">ROI</p>
                              <p className="text-xl font-bold text-green-600">
                                {(scenario.roi * 100).toFixed(1)}%
                              </p>
                            </div>

                            <div className="border-t border-gray-200 pt-3">
                              <p className="text-xs text-gray-600 font-medium mb-2">Channel Contributions</p>
                              <div className="space-y-1">
                                {Object.entries(scenario.contributions).map(([channel, contribution]) => (
                                  <div key={channel} className="flex items-center justify-between text-sm">
                                    <span className="text-gray-700">{channel}</span>
                                    <span className="font-medium text-gray-900">
                                      ${(contribution as number).toLocaleString('en-US', {
                                        maximumFractionDigits: 0,
                                      })}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <div className="border-t border-gray-200 pt-3">
                              <p className="text-xs text-gray-600 font-medium mb-2">Budget Allocation</p>
                              <div className="space-y-1">
                                {Object.entries(scenario.allocation).map(([channel, allocation]) => (
                                  <div key={channel} className="flex items-center justify-between text-sm">
                                    <span className="text-gray-700">{channel}</span>
                                    <span className="font-medium text-gray-900">
                                      ${(allocation as number).toLocaleString('en-US', {
                                        maximumFractionDigits: 0,
                                      })}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {scenarios.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" /> Key Insights
                      </h4>
                      <ul className="text-sm text-blue-800 space-y-2">
                        {scenarios[0] && scenarios[1] && (
                          <>
                            <li>
                              • The <strong>{scenarios[0].predicted_response > scenarios[1].predicted_response ? 'Recommended' : 'Custom'}</strong> allocation shows {
                                Math.abs((Math.max(scenarios[0].predicted_response, scenarios[1].predicted_response) / Math.min(scenarios[0].predicted_response, scenarios[1].predicted_response) - 1) * 100).toFixed(1)
                              }% higher predicted revenue
                            </li>
                            <li>
                              • ROI difference: {Math.abs(scenarios[0].roi - scenarios[1].roi).toFixed(3)}
                            </li>
                          </>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 flex gap-3 justify-end">
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
